import React, { useMemo, useRef, useEffect, useCallback, useContext } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import {
	steps,
	useIssueBreakdown,
} from '@atlassian/jira-ai-work-breakdown/src/controllers/context.tsx';

import { ISSUE_VIEW_CONSUMER } from '@atlassian/jira-ai-work-breakdown/src/constants.tsx';
import { TEAM_MANAGED_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	useChildIssuesLimit,
	useIsAiEnabledForIssue,
	useIssueId,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import type { ServerAssociatedIssue } from '@atlassian/jira-issue-shared-types/src/common/types/associated-issue-type.tsx';
import { ProgressSummary as ProgressSummaryRelay } from '@atlassian/jira-issue-view-child-issues-panel/src/ui/progress-summary/index.tsx';
import {
	EXCEEDED_LIMIT_AFTER_LOAD,
	EXCEEDED_LIMIT_INITIAL_LOAD,
	WITHIN_LIMIT,
	GIC_CALLBACK_PAYLOAD_INLINE_CREATE_ID,
} from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';

import { getChildIssuesLimitExceededAnalyticsCount } from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { useTriggerIssueViewModal } from '@atlassian/jira-open-issue-view-modal-controller/src/index.tsx';
import { CHILDREN_WITHIN_LIMIT_GQL_FIELD } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import type { view_issueViewCommonViews_ChildIssuesPanelBaseWithFragment$key } from '@atlassian/jira-relay/src/__generated__/view_issueViewCommonViews_ChildIssuesPanelBaseWithFragment.graphql';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	useAiWorkBreakDownAnalytics,
	ISSUE_BREAKDOWN_PROACTIVE_CHILD_PANEL_ADD_ID,
} from '@atlassian/jira-ai-work-breakdown/src/common/analytics/index.tsx';
import type { view_issueViewCommonViews_ChildIssuesPanelBaseWithRanking$key } from '@atlassian/jira-relay/src/__generated__/view_issueViewCommonViews_ChildIssuesPanelBaseWithRanking.graphql';
import { ChildIssuesPanelSkeletonNew } from '@atlassian/jira-skeletons/src/ui/child-issues-panel/components/ChildIssuesPanelSkeleton.tsx';
import { getChildIssuesLimit } from '@atlassian/jira-issue-view-common-constants/src/index.tsx';
import type { view_issueViewCommonViews_ChildIssuePanelBaseWithIssueSearchTotalCount$key } from '@atlassian/jira-relay/src/__generated__/view_issueViewCommonViews_ChildIssuePanelBaseWithIssueSearchTotalCount.graphql';
import { AiIssueBreakdownEntryPointContext } from '@atlassian/jira-ai-work-breakdown/src/controllers/context-provider.tsx';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { CHILD_ISSUES_PANEL_TYPE } from '../common/constants.tsx';
import {
	hasExceededIssuesLimitInitialLoad,
	hasExceededIssuesLimitAfterLoad as hasExceededIssuesLimitAfterLoadUtil,
} from '../common/utils/has-exceeded-issues-limit/index.tsx';
import transfromServerChild from '../common/utils/transform-server-child/index.tsx';

import { useChildPanelRenderTrigger } from '../controllers/child-panel-render-trigger/index.tsx';
import { useShouldProactivelySuggestIssues } from '../controllers/use-should-proactively-suggest-issues/index.tsx';
import { useShouldTriggerAiSuggestedIssues } from '../controllers/main.tsx';
import { CHILD_PANEL_VIEWS } from '../state/ui/actions.tsx';
import { AiIssueBreakdownEntryPointContainer } from './ai-issue-breakdown/index.tsx';
import { ChildIssuesLimitFlag } from './child-issues-limit-flag/index.tsx';
import { ChildIssuesLimitPanel } from './child-issues-limit-panel/index.tsx';
import { AsyncConfigurableChildIssuesPanel } from './configurable-child-issues-panel/src/ui/async.tsx';
import type { AddChildEventHandler } from './heading/add-child/types.tsx';
import { HideDoneIconWithToolTip } from './hide-done-icon-with-tooltip/index.tsx';
import { getIsSubtaskCreateEnabled, useHideDoneChildIssues } from './utils.tsx';
import type { Props } from './types.tsx';

// Only show progress summary when there are at least two child issues (or subtasks).
const PROGRESS_SUMMARY_MIN_ISSUE_COUNT = 2;

export const useAiIssueBreakdownEntryPointActions = (): EntryPointActions =>
	useContext(AiIssueBreakdownEntryPointContext).entryPointActions;

const ChildIssuesPanelBase = ({
	hasExceededIssuesLimitAfterLoad,
	totalChildIssueCount,
	addClickCount = 0,
	Heading,
	Issues,
	InlineCreate,
	ProgressSummary,
	AddExistingIssue,
	allIssues,
	childPanelView,
	sourceName,
	childIssuesLimitUrl,
	onAddChildClick,
	onIssueBreakdownClick,
	childIssuesPanelType,
	issueHierarchyLevel,
	epicLinkFieldKey,
	parentIssueKey,
	parentIssueTypeId,
	projectId,
	baseUrl,
	childIssueTypes,
	issueTypeWithWorkflowValidators,
	onFullDialogCreate,
	onSuggestedChildIssueCreateSuccess,
	incompleteIssues,
	filterSubtasks,
	rootRelayFragment,
	shouldProactivelySuggestIssues = false,
	isRankingEnabled,
	isHideDoneFilterEnabled,
	hasMultipleChildPanels,
	issueSearchTotalCount = 0,
}: Props) => {
	const [{ issueCreateSessionId }] = useTriggerIssueViewModal();
	const initialTotalChildIssueCount = useRef<number>(totalChildIssueCount);
	const columnPickerRef = useRef<HTMLDivElement>(null);

	const childIssuesLimitStatus = useMemo(() => {
		if (childIssuesLimitUrl) {
			return EXCEEDED_LIMIT_INITIAL_LOAD;
		}

		if (hasExceededIssuesLimitAfterLoad) {
			return EXCEEDED_LIMIT_AFTER_LOAD;
		}

		return WITHIN_LIMIT;
	}, [childIssuesLimitUrl, hasExceededIssuesLimitAfterLoad]);

	const shouldTriggerAiSuggestedIssues = useShouldTriggerAiSuggestedIssues();

	const entryPointActions = useAiIssueBreakdownEntryPointActions();

	const issueId = useIssueId();

	const projectKey = useProjectKey(parentIssueKey || '');

	const [{ whichChildPanelRenderTriggered }, { resetWhichChildPanelRenderTriggered }] =
		useChildPanelRenderTrigger();

	const hasExceededChildIssuesLimitInitialLoad = useMemo(
		() => hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
		[childIssuesLimitStatus],
	);

	const hasExceededChildIssuesLimitAfterLoad = useMemo(
		() => hasExceededIssuesLimitAfterLoadUtil(childIssuesLimitStatus),
		[childIssuesLimitStatus],
	);

	const hasContent =
		hasExceededChildIssuesLimitInitialLoad ||
		allIssues.length > 0 ||
		addClickCount > 0 ||
		shouldTriggerAiSuggestedIssues ||
		whichChildPanelRenderTriggered ||
		issueSearchTotalCount > 0;

	const loadingAllIssues = allIssues.length === 0 && issueSearchTotalCount > 0;
	const willHaveAnyIssues = allIssues.length > 0 || issueSearchTotalCount > 0;

	const childIssuesLimit = useChildIssuesLimit();
	const approachingLimitCutOff = childIssuesLimit - 1;

	const startedBelowTheCutoff =
		!hasExceededChildIssuesLimitInitialLoad &&
		initialTotalChildIssueCount.current < approachingLimitCutOff;

	const isNowAtTheCutoff = totalChildIssueCount === approachingLimitCutOff;

	const isApproachingLimit = startedBelowTheCutoff && isNowAtTheCutoff;

	const { shouldShowHideDoneInfoIcon, completedIssuesCount } = useHideDoneChildIssues(
		allIssues,
		incompleteIssues,
	);

	const [
		{ currentStep, channelId },
		{
			resetIssueBreakdownState,
			isIssueBreakdownOpen,
			setIssueBreakdownStep,
			setIsProactivelySuggestingIssues,
		},
	] = useIssueBreakdown();

	const isAIWBPanelLoaded = useRef(false);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		/*
		AIWB panel is async loaded
		- on hover of suggest subtasks option in suggest issues DD
		- on hover of suggest subtasks button in child issue panel
        - in AIWB button component (similar to below, manually triggering when AIWB step changes)
		proactive_aiwb_2 is redesigning the UI & AIWB button is removed from child panel, hence loading entrypoints here when a flow needs AIWB panel
		*/
		if (
			!isAIWBPanelLoaded.current &&
			currentStep !== steps.defaultStep &&
			expVal('proactive_aiwb_2', 'enabled', false)
		) {
			entryPointActions.load();
			isAIWBPanelLoaded.current = true;
		}
	}, [entryPointActions, currentStep]);
	const isAiOptInEnabled = useIsAiEnabledForIssue();

	const { fireTrack } = useAiWorkBreakDownAnalytics({ channelId, consumer: ISSUE_VIEW_CONSUMER });

	const onAddChildClickWithAiBreakdownEnabled = useCallback<AddChildEventHandler>(
		(e, analyticsEvent) => {
			if (isAiOptInEnabled) {
				if (expVal('proactive_aiwb_2', 'enabled', false)) {
					setIsProactivelySuggestingIssues(shouldProactivelySuggestIssues);
					if (!isIssueBreakdownOpen()) {
						setIssueBreakdownStep(steps.draftListStep);
						shouldProactivelySuggestIssues &&
							fireTrack('aiInteraction initiated', 'proactiveFromQuickAddClick', {
								invokedFrom: ISSUE_BREAKDOWN_PROACTIVE_CHILD_PANEL_ADD_ID,
								proactiveAIGenerated: 1,
							});
					}
				} else {
					resetIssueBreakdownState();
				}
			}
			onAddChildClick && onAddChildClick(e, analyticsEvent);
		},
		[
			fireTrack,
			isAiOptInEnabled,
			isIssueBreakdownOpen,
			onAddChildClick,
			resetIssueBreakdownState,
			setIsProactivelySuggestingIssues,
			setIssueBreakdownStep,
			shouldProactivelySuggestIssues,
		],
	);

	const ProgressSummaryWithHideDoneIconContainer = expVal(
		'jira-issue-view-chin-ga',
		'isChildIssueEnabled',
		false,
	)
		? ProgressSummaryWithHideDoneIconContainerCHIN
		: ProgressSummaryWithHideDoneIconContainerOld;

	const renderIssues = useMemo(() => {
		if (fg('improve_rendering_child_issues_summary_bar')) {
			return null;
		}

		const progressSummary =
			!hasExceededChildIssuesLimitInitialLoad &&
			(expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
				? true
				: allIssues.length >= PROGRESS_SUMMARY_MIN_ISSUE_COUNT) ? (
				<ProgressSummary />
			) : null;

		const newChildIssues = () =>
			loadingAllIssues && expVal('issue_view_ssr_perf_m2', 'isEnabled', false) ? (
				<ChildIssuesPanelSkeletonNew rows={totalChildIssueCount} maxRows={getChildIssuesLimit()} />
			) : (
				<UFOSegment name="issue-child-issues-table">
					<AsyncConfigurableChildIssuesPanel
						columnPickerRef={columnPickerRef}
						parentIssueTypeId={parentIssueTypeId}
						projectId={projectId}
						filterSubtasks={filterSubtasks}
						rootRelayFragment={rootRelayFragment}
						totalChildIssueCount={totalChildIssueCount}
						isHideDoneFilterEnabled={isHideDoneFilterEnabled}
						sourceName={sourceName}
					/>
				</UFOSegment>
			);

		const childIssues = () =>
			expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false) ? (
				newChildIssues()
			) : (
				<UFOSegment name="issue-child-issues-list">
					<Issues
						hasExceededChildIssuesLimitOnLoadOrAfter={
							hasExceededChildIssuesLimitAfterLoad || hasExceededChildIssuesLimitInitialLoad
						}
					/>
				</UFOSegment>
			);

		return willHaveAnyIssues ? (
			<>
				<ProgressSummaryWithHideDoneIconContainer>
					{progressSummary}
					{shouldShowHideDoneInfoIcon && (
						<HideDoneIconWithToolTip completedChildIssuesCount={completedIssuesCount} />
					)}
				</ProgressSummaryWithHideDoneIconContainer>
				{childIssues()}
			</>
		) : null;
		// Excluded ProgressSummary, projectKey, Issues, issueId as the component and analytic variable are not needed in the dependency
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		shouldShowHideDoneInfoIcon,
		completedIssuesCount,
		allIssues.length,
		projectId,
		parentIssueTypeId,
		incompleteIssues.length,
		hasExceededChildIssuesLimitInitialLoad,
		hasExceededChildIssuesLimitAfterLoad,
		filterSubtasks,
	]);

	const { subscribe } = useGICLifeCycleEvents();
	const isSubtaskCreateEnabled = getIsSubtaskCreateEnabled(
		childIssuesPanelType,
		issueHierarchyLevel,
	);

	useEffect(
		() =>
			subscribe?.(GIC_LIFECYCLE_EVENTS.ISSUE_CREATE_SUCCESS, async (payload) => {
				if (payload.callbackPayload?.id !== GIC_CALLBACK_PAYLOAD_INLINE_CREATE_ID) return;
				const { createdIssueDetails } = payload.createdIssueData;

				const callbackData = payload.callbackPayload?.data;

				if (
					callbackData.parentIssueKey !== parentIssueKey ||
					callbackData.childIssuesPanelType !== childIssuesPanelType
				)
					return;

				if (
					isSubtaskCreateEnabled ||
					(epicLinkFieldKey && createdIssueDetails.fields[epicLinkFieldKey]) === parentIssueKey ||
					callbackData?.parentIssueKey === parentIssueKey
				) {
					onFullDialogCreate(
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						callbackData.optimisticId as string,
						transfromServerChild(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							createdIssueDetails as unknown as ServerAssociatedIssue,
							baseUrl,
						),
						{ sessionId: issueCreateSessionId },
					);
				}
			}),
		[
			subscribe,
			baseUrl,
			epicLinkFieldKey,
			onFullDialogCreate,
			parentIssueKey,
			childIssuesPanelType,
			isSubtaskCreateEnabled,
			issueCreateSessionId,
		],
	);

	const renderCreate = useMemo(() => {
		const isAiEnabledAndOpen = isAiOptInEnabled && isIssueBreakdownOpen();

		// Create panel should not render if AI issue breakdown is open when proactive suggestions experiment is disabled
		// when enabled should render both inline create & AI issue breakdown
		if (isAiEnabledAndOpen && !expVal('proactive_aiwb_2', 'enabled', false)) {
			return;
		}

		if (addClickCount > 0) {
			switch (childPanelView) {
				case CHILD_PANEL_VIEWS.InlineCreate:
					return <InlineCreate inputId={sourceName} />;
				case CHILD_PANEL_VIEWS.AddExistingIssue:
					return <AddExistingIssue />;
				default:
					return null;
			}
		}
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		addClickCount,
		childPanelView,
		sourceName,
		currentStep,
		isIssueBreakdownOpen,
		shouldProactivelySuggestIssues,
	]);

	const childIssuesLimitExceededAnalyticsCount =
		getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit);

	const renderAIWorkBreakdown = useMemo(() => {
		if (childIssueTypes.length === 0) return null;

		const childIssueTypeWithWFValidator = childIssueTypes.map((issueType) => ({
			...issueType,
			hasWorkflowValidators:
				(issueTypeWithWorkflowValidators && issueTypeWithWorkflowValidators[issueType.id]) || false,
		}));
		return (
			<AiIssueBreakdownEntryPointContainer
				onInit={onIssueBreakdownClick}
				onCreate={onSuggestedChildIssueCreateSuccess}
				childIssueTypes={childIssueTypeWithWFValidator}
				isSubTaskCreationOpen={isSubtaskCreateEnabled}
				issueHierarchyLevel={issueHierarchyLevel}
				onDiscard={resetWhichChildPanelRenderTriggered}
				projectId={projectId}
				issueKey={parentIssueKey || ''}
				issueId={issueId}
				projectKey={projectKey}
				consumer={ISSUE_VIEW_CONSUMER}
			/>
		);
	}, [
		childIssueTypes,
		onIssueBreakdownClick,
		onSuggestedChildIssueCreateSuccess,
		isSubtaskCreateEnabled,
		issueHierarchyLevel,
		resetWhichChildPanelRenderTriggered,
		projectId,
		parentIssueKey,
		issueId,
		projectKey,
		issueTypeWithWorkflowValidators,
	]);

	if (!hasContent) {
		return null;
	}

	const shouldDisplayProgressSummary =
		!hasExceededChildIssuesLimitInitialLoad &&
		(expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false) ||
			allIssues.length >= PROGRESS_SUMMARY_MIN_ISSUE_COUNT);

	return (
		<ContextualAnalyticsData
			attributes={{
				childCount: hasExceededChildIssuesLimitInitialLoad
					? childIssuesLimitExceededAnalyticsCount
					: allIssues.length,
				isChildIssuesLimitPanelDisplayed: hasExceededChildIssuesLimitInitialLoad,
			}}
		>
			<FireScreenAnalytics actionSubjectId={sourceName} />
			<Heading
				columnPickerRef={columnPickerRef}
				onAddChildClick={onAddChildClickWithAiBreakdownEnabled}
				sourceName={sourceName}
				childIssuesLimitStatus={childIssuesLimitStatus}
				issueHierarchyLevel={issueHierarchyLevel}
				rootRelayFragment={rootRelayFragment}
				isHideDoneFilterEnabled={
					// Precondition needed as hide-rank-option-when-unavailable has the root relay fragment where we grab the value from
					// eslint-disable-next-line jira/ff/no-preconditioning
					fg('hide-rank-option-when-unavailable') && fg('key-chin-hide-done-relay-migration')
						? isHideDoneFilterEnabled
						: undefined
				}
				isRankingEnabled={fg('hide-rank-option-when-unavailable') ? isRankingEnabled : undefined}
				hasMultipleChildPanels={hasMultipleChildPanels}
			/>
			{hasExceededChildIssuesLimitInitialLoad && (
				<ChildIssuesLimitPanel
					hasExceededIssuesLimitInitialLoad={hasExceededChildIssuesLimitInitialLoad}
				/>
			)}
			<ChildIssuesLimitFlag
				isApproachingLimit={isApproachingLimit}
				childIssuesLimitStatus={childIssuesLimitStatus}
			/>
			{expVal('proactive_aiwb_2', 'enabled', false) && renderAIWorkBreakdown}
			{willHaveAnyIssues && fg('improve_rendering_child_issues_summary_bar') && (
				<>
					<ProgressSummaryWithHideDoneIconContainer>
						{shouldDisplayProgressSummary && <ProgressSummary />}
						{shouldShowHideDoneInfoIcon && (
							<HideDoneIconWithToolTip completedChildIssuesCount={completedIssuesCount} />
						)}
					</ProgressSummaryWithHideDoneIconContainer>
					{expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false) && (
						<UFOSegment name="issue-child-issues-table">
							{loadingAllIssues && expVal('issue_view_ssr_perf_m2', 'isEnabled', false) ? (
								<ChildIssuesPanelSkeletonNew
									rows={totalChildIssueCount}
									maxRows={getChildIssuesLimit()}
								/>
							) : (
								<AsyncConfigurableChildIssuesPanel
									columnPickerRef={columnPickerRef}
									parentIssueTypeId={parentIssueTypeId}
									projectId={projectId}
									filterSubtasks={filterSubtasks}
									rootRelayFragment={rootRelayFragment}
									totalChildIssueCount={totalChildIssueCount}
									isHideDoneFilterEnabled={isHideDoneFilterEnabled}
									sourceName={sourceName}
								/>
							)}
						</UFOSegment>
					)}
					{!expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false) && (
						<UFOSegment name="issue-child-issues-list">
							<Issues
								hasExceededChildIssuesLimitOnLoadOrAfter={
									hasExceededChildIssuesLimitAfterLoad || hasExceededChildIssuesLimitInitialLoad
								}
							/>
						</UFOSegment>
					)}
				</>
			)}
			{renderIssues}
			{renderCreate && expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false) ? (
				<Box xcss={createChildIssueContainerStyles}>{renderCreate}</Box>
			) : (
				renderCreate
			)}
			{!expVal('proactive_aiwb_2', 'enabled', false) && renderAIWorkBreakdown}
		</ContextualAnalyticsData>
	);
};
const ChildIssuesPanelBaseWithRanking = (props: Props) => {
	const { allIssues, childIssuesPanelType, rootRelayFragment } = props;

	// eslint-disable-next-line @atlassian/relay/query-restriction
	const data = useFragment<view_issueViewCommonViews_ChildIssuesPanelBaseWithRanking$key>(
		graphql`
			fragment view_issueViewCommonViews_ChildIssuesPanelBaseWithRanking on JiraQuery {
				rankField(cloudId: $cloudId) {
					jqlTerm
				}
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					isIssueViewHideDoneChildIssuesFilterEnabled
				}
				issueByKey(cloudId: $cloudId, key: $issueKey) {
					projectField {
						project {
							projectStyle
						}
					}
				}
			}
		`,
		rootRelayFragment?.jira ?? null,
	);

	const isTeamManagedProject =
		data?.issueByKey?.projectField?.project?.projectStyle === TEAM_MANAGED_PROJECT;

	const isHideDoneFilterEnabled =
		data?.userPreferences?.isIssueViewHideDoneChildIssuesFilterEnabled === true;

	const isRankingEnabled =
		// Issue ranking is only supported in CMP Subtasks and TMP Child Issues
		(childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CLASSIC_SUBTASKS ||
			(isTeamManagedProject &&
				childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CHILDREN_ISSUES_PANEL)) &&
		// Should have at least 2 issues
		allIssues.length > 1 &&
		// All issues should be visible
		!isHideDoneFilterEnabled &&
		// Rank field should be available
		data?.rankField?.jqlTerm != null;

	return (
		<ChildIssuesPanelBase
			{...props}
			isRankingEnabled={isRankingEnabled}
			isHideDoneFilterEnabled={isHideDoneFilterEnabled}
		/>
	);
};

const MaybeChildIssuesPanelBaseWithRanking = componentWithFG(
	'hide-rank-option-when-unavailable',
	ChildIssuesPanelBaseWithRanking,
	ChildIssuesPanelBase,
);

const ChildIssuesPanelBaseWithFragment = (props: Props) => {
	// This will be addressed once we move this into its own package
	// eslint-disable-next-line @atlassian/relay/query-restriction
	const data = useFragment<view_issueViewCommonViews_ChildIssuesPanelBaseWithFragment$key>(
		graphql`
			fragment view_issueViewCommonViews_ChildIssuesPanelBaseWithFragment on JiraQuery {
				issueByKey(key: $issueKey, cloudId: $cloudId) {
					childIssues {
						__typename
						... on JiraChildIssuesWithinLimit {
							issues(activeProjectsOnly: true, activeIssuesOnly: true) {
								...progressSummary_issueViewChildIssuesPanel
							}
						}
					}
				}
			}
		`,
		props.rootRelayFragment?.jira ?? null,
	);

	const issues =
		data?.issueByKey?.childIssues?.__typename === CHILDREN_WITHIN_LIMIT_GQL_FIELD
			? data.issueByKey.childIssues.issues
			: null;

	return (
		<MaybeChildIssuesPanelBaseWithRanking
			{...props}
			ProgressSummary={() => (
				<ProgressSummaryRelay
					issues={issues ?? null}
					isClassicSubtaskPanel={
						props.childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CLASSIC_SUBTASKS
					}
				/>
			)}
		/>
	);
};

const MaybeChildIssuesPanelBaseWithFragment = componentWithFG(
	'issue-jiv-20335-relay-progress-summary',
	ChildIssuesPanelBaseWithFragment,
	MaybeChildIssuesPanelBaseWithRanking,
);

const ChildIssuePanelWithProactiveParam = (props: Props) => {
	const shouldProactivelySuggestIssues = useShouldProactivelySuggestIssues(
		props.totalChildIssueCount,
	);
	return (
		<MaybeChildIssuesPanelBaseWithFragment
			{...props}
			shouldProactivelySuggestIssues={shouldProactivelySuggestIssues}
		/>
	);
};

export const MaybeChildIssuePanelWithProactiveParam = componentWithFG(
	'proactive_aiwb_2_targeting_gate',
	ChildIssuePanelWithProactiveParam,
	MaybeChildIssuesPanelBaseWithFragment,
);

const ChildIssuePanelBaseWithIssueSearchTotalCount = (props: Props) => {
	const data =
		// eslint-disable-next-line @atlassian/relay/query-restriction
		useFragment<view_issueViewCommonViews_ChildIssuePanelBaseWithIssueSearchTotalCount$key>(
			graphql`
				fragment view_issueViewCommonViews_ChildIssuePanelBaseWithIssueSearchTotalCount on JiraQuery
				@argumentDefinitions(
					issueViewSsrMerfM2IsEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/issue-view-ssr-perf-m2-is-enabled.relayprovider"
					}
				) {
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						fieldSetsInput: $fieldSetsInput
					) @optIn(to: "JiraSpreadsheetComponent-M1") @include(if: $issueViewSsrMerfM2IsEnabled) {
						totalCount
					}
				}
			`,
			props.rootRelayFragment?.jira ?? null,
		);

	const issueSearchTotalCount = data?.issueSearch?.totalCount ?? 0;

	return (
		<MaybeChildIssuePanelWithProactiveParam
			{...props}
			issueSearchTotalCount={issueSearchTotalCount}
		/>
	);
};

export const ChildIssuesPanel = componentWithCondition(
	() => expVal('issue_view_ssr_perf_m2', 'isEnabled', false),
	ChildIssuePanelBaseWithIssueSearchTotalCount,
	MaybeChildIssuePanelWithProactiveParam,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressSummaryWithHideDoneIconContainerOld = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressSummaryWithHideDoneIconContainerCHIN = styled.div({
	display: 'flex',
	marginBottom: token('space.100'),
});

const createChildIssueContainerStyles = xcss({
	marginTop: 'space.100',
});
